// components/ErrorBoundary.tsx - Error Boundary Component for Gate Pass Form

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({ error, errorInfo });
    
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

// Default error fallback component
interface ErrorFallbackProps {
  error?: Error;
  onRetry: () => void;
  onReload: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, onRetry, onReload }) => (
  <div style={{
    backgroundColor: '#f8fafc',
    minHeight: '100vh',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #fecaca',
      borderRadius: '12px',
      padding: '40px',
      maxWidth: '600px',
      textAlign: 'center'
    }}>
      {/* Error Icon */}
      <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
      
      {/* Error Title */}
      <h2 style={{ 
        margin: '0 0 16px 0', 
        color: '#dc2626', 
        fontSize: '24px',
        fontWeight: '600' 
      }}>
        Something went wrong
      </h2>
      
      {/* Error Message */}
      <p style={{ 
        margin: '0 0 24px 0', 
        color: '#64748b',
        fontSize: '16px',
        lineHeight: '1.5'
      }}>
        An unexpected error occurred while loading the Gate Pass form. 
        Please try refreshing the page or contact support if the problem persists.
      </p>
      
      {/* Error Details (in development) */}
      {process.env.NODE_ENV === 'development' && error && (
        <details style={{
          marginBottom: '24px',
          textAlign: 'left',
          backgroundColor: '#f1f5f9',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <summary style={{ 
            cursor: 'pointer', 
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>
            Error Details (Development)
          </summary>
          <pre style={{ 
            fontSize: '12px',
            color: '#dc2626',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: 0,
            fontFamily: 'monospace'
          }}>
            {error.message}
            {error.stack && `\n\nStack trace:\n${error.stack}`}
          </pre>
        </details>
      )}
      
      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        <button
          onClick={onRetry}
          style={{
            padding: '12px 24px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          Try Again
        </button>
        
        <button
          onClick={onReload}
          style={{
            padding: '12px 24px',
            backgroundColor: '#f8fafc',
            color: '#475569',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          Reload Page
        </button>
      </div>
      
      {/* Support Information */}
      <div style={{
        marginTop: '32px',
        padding: '16px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '8px'
      }}>
        <p style={{ 
          margin: 0, 
          fontSize: '14px',
          color: '#1e40af'
        }}>
          <strong>Need help?</strong> If this error persists, please contact the IT support team 
          with the error details above.
        </p>
      </div>
    </div>
  </div>
);

// Specific error boundary for form sections
export const FormSectionErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, ...props }) => (
  <ErrorBoundary
    {...props}
    fallback={
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        padding: '24px',
        textAlign: 'center',
        margin: '20px 0'
      }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
        <h4 style={{ margin: '0 0 8px 0', color: '#dc2626' }}>
          Form Section Error
        </h4>
        <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '14px' }}>
          This section of the form encountered an error. You can continue with other sections.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '8px 16px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Reload Form
        </button>
      </div>
    }
  >
    {children}
  </ErrorBoundary>
);

// Hook for handling errors in functional components
export const useErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);

  const handleError = React.useCallback((error: Error) => {
    console.error('Error caught by useErrorHandler:', error);
    setError(error);
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  // Throw error to be caught by error boundary
  if (error) {
    throw error;
  }

  return { handleError, clearError };
};